import { computed, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { AccountRole, PasswordUpdateResponse, UserRole } from '../models/app.models';
import { environment } from '../../environments/environment';
export interface LoginResponse {
  success: boolean;
  role: UserRole;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Stores the role confirmed by the backend.
  private readonly _role = signal<UserRole>(null);

  // Components can read the role but cannot modify the signal directly.
  readonly role = this._role.asReadonly();

  readonly isLoggedIn = computed(() => this._role() !== null);

  // Individual role checks.
  readonly isAdmin = computed(() => this._role() === 'Admin');
  readonly isManager = computed(() => this._role() === 'Manager');
  readonly isCrew = computed(() => this._role() === 'Crew');

  // All authenticated roles can access the Crew Portal.
  readonly canAccessCrewPortal = computed(() =>
    this._role() === 'Admin' ||
    this._role() === 'Manager' ||
    this._role() === 'Crew'
  );

  // Admin and Manager can access the Manager Portal.
  readonly canAccessManagerPortal = computed(() =>
    this._role() === 'Admin' ||
    this._role() === 'Manager'
  );

  // Only Admin can access the Admin Portal.
  readonly canAccessAdminPortal = computed(() =>
    this._role() === 'Admin'
  );

  constructor(private readonly http: HttpClient) {}

  login(username: string, password: string) {
    return this.http.post<LoginResponse>(
      environment.apiUrl +'/auth/login',
      { username, password },
      {
        // Allows the browser to receive the authentication cookie.
        withCredentials: true
      }
    ).pipe(
      // Store only the role returned by the authenticated backend.
      tap(response => this._role.set(response.role))
    );
  }

  // Currently clears only the frontend authentication state.
  logout(): void {
    this._role.set(null);
  }

  hasAccess(requiredRole: 'Admin' | 'Manager' | 'Crew'): boolean {
    const currentRole = this.role();

    const roleLevel = {
      Crew: 1,
      Manager: 2,
      Admin: 3
    };

    if (!currentRole) {
      return false;
    }

    return roleLevel[currentRole] >= roleLevel[requiredRole];
  }
  updatePassword(
    role: AccountRole,
    currentPassword: string,
    newPassword: string
  ): Observable<PasswordUpdateResponse> {
    return this.http.put<PasswordUpdateResponse>(
      `${environment.apiUrl}/auth/password`,
      {
        role,
        currentPassword,
        newPassword
      },
      {
        withCredentials: true
      }
    );
  }
}